// « Ce que vos offres demandent et que votre CV ne dit pas » (lot 3 de la
// refonte, page « Mon CV »). Chaque manque est nommé par les employeurs qui le
// demandent, et « Je l'ai » l'écrit dans le CV (services/manquesCv.ts).

import { Tags } from 'lucide-react'
import { get, post } from '../lib/api'
import { useT } from '../lib/i18n'
import { useLang } from '../lib/lang'
import { LOCALE_UI } from '@ai-job-fit/engine/langues'
import { useData, ActionButton, PastilleTitre, useToast } from './common'

/** Cf. `src/services/marche.ts`. */
interface Marche {
  offres: number
  employeurs: number
  salaireMin: number | null
  salaireMax: number | null
}

interface Manque {
  mot: string
  n: number
  employeurs: string[]
  annonces: boolean
}

export function ManquesCv({ onChanged }: { onChanged: () => void }) {
  const t = useT()
  const lang = useLang()
  const toast = useToast()
  const data = useData(
    () => get<{ offres: number; manques: Manque[]; marche?: Marche }>('/api/radar/manques'),
    []
  )
  const d = data.data
  if (!d || (d.manques.length === 0 && !d.marche?.offres)) return null
  const euros = (n: number) => n.toLocaleString(LOCALE_UI[lang])
  // Deux phrases courtes, pluriels justes, et pas de « chez 0 employeurs »
  // quand aucun employeur n'est connu (issue #148). Même forme que le récap
  // du lundi (`phraseMarche`, services/marche.ts).
  const m = d.marche
  const marche = m?.offres
    ? [
        [
          m.offres === 1 ? t('marche.offres1') : t('marche.offres', { n: m.offres }),
          ...(m.employeurs ? [m.employeurs === 1 ? t('marche.chez1') : t('marche.chez', { e: m.employeurs })] : []),
        ].join(' ') + '.',
        ...(m.salaireMin && m.salaireMax ? [t('marche.salaire', { min: euros(m.salaireMin), max: euros(m.salaireMax) })] : []),
      ].join(' ')
    : null

  const chez = (m: Manque) =>
    [...m.employeurs, ...(m.annonces ? [t('manques.sitesAnnonces')] : [])].join(', ')

  return (
    <div className="panel">
      <h4><PastilleTitre h={150}><Tags size={16} /></PastilleTitre>{t('manques.titre')}</h4>
      {marche && <p className="pnote"><b>{marche}</b></p>}
      {d.manques.length > 0 && <p className="pnote">{t('manques.intro', { n: d.offres })}</p>}
      <div className="acc-liste manques">
        {d.manques.map((m) => (
          <div className="acc-ligne" key={m.mot}>
            <span className="manque-n" title={t('manques.nAria', { n: m.n })}>{m.n}</span>
            <span className="grow">
              <b>{m.mot}</b>
              {chez(m) && <span className="acc-etat calme">{t('manques.chez', { liste: chez(m) })}</span>}
            </span>
            <ActionButton
              size="sm"
              onClick={async () => {
                const r = await post<{ ajoute?: boolean }>('/api/radar/manques', { mot: m.mot })
                const annuler = r?.ajoute
                  ? {
                      label: t('manques.annuler'),
                      onClick: async () => {
                        await post('/api/radar/manques', { mot: m.mot, retirer: true })
                        toast(t('manques.retire', { mot: m.mot }))
                        data.reload()
                        onChanged()
                      },
                    }
                  : undefined
                toast(t('manques.ajoute', { mot: m.mot }), 'ok', annuler)
                data.reload()
                onChanged()
              }}
            >
              {t('manques.jeLai')}
            </ActionButton>
          </div>
        ))}
      </div>
    </div>
  )
}
